import React, { useCallback } from 'react';

interface PaneResizeHandleProps {
  /** Accessible name, e.g. "Resize the playbook and insights panes". */
  label: string;
  /** Called with the pointer's x while the handle is dragged. */
  onDrag: (clientX: number) => void;
  /** The arrow keys: -1 for left, 1 for right. */
  onStep: (direction: -1 | 1) => void;
}

/**
 * The drag handle between two live-call panes, the same on macOS and WPF
 * (owner, 2026-10-26): the 8px gap between the panes, a hairline down its middle
 * and a small three-dot grip so it reads as draggable. Drag it, or focus it and
 * use the arrow keys.
 */
const PaneResizeHandle: React.FC<PaneResizeHandleProps> = ({ label, onDrag, onStep }) => {
  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    document.body.style.userSelect = 'none';
    const move = (moveEvent: PointerEvent) => onDrag(moveEvent.clientX);
    const end = (endEvent: PointerEvent) => {
      if (handle.hasPointerCapture(endEvent.pointerId)) handle.releasePointerCapture(endEvent.pointerId);
      document.body.style.userSelect = '';
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }, [onDrag]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      onStep(event.key === 'ArrowLeft' ? -1 : 1);
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      className="group relative flex w-2 flex-shrink-0 cursor-col-resize touch-none items-center justify-center focus-visible:outline-none"
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-hairline transition-colors group-hover:bg-ink-border group-focus-visible:bg-signature"
      />
      <span aria-hidden="true" className="relative flex flex-col gap-0.5 rounded-full bg-paper py-1">
        {[0, 1, 2].map((dot) => (
          <span key={dot} className="block h-0.5 w-0.5 rounded-full bg-ink-muted" />
        ))}
      </span>
    </div>
  );
};

export default PaneResizeHandle;
